import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ApplicationState } from 'store';
import {
  createChatMessages,
  fetchChat,
  fetchChatMessages,
} from 'store/modules/bidding_chat/actions';
import {
  BiddingChatMessageTypes,
  BiddingChatState,
  BiddingChatTypes,
} from 'store/modules/bidding_chat/types';

import tabId from 'services/tabId';

const MAX_FETCH_MESSAGES_RETRIES = 3;

interface UseBiddingChatSessionParams {
  biddingId: number;
  active: boolean;
  clearOnBiddingChange?: boolean;
}

export interface BiddingChatSession {
  messages: BiddingChatMessageTypes[] | undefined;
  isLoadingChat: boolean;
  isLoadingMessage: boolean;
  hasNoContent: boolean;
  hasFetchMessagesError: boolean;
  messagesRef: React.RefObject<HTMLDivElement>;
  inputRef: React.MutableRefObject<HTMLInputElement>;
  sendMessage: (message?: string, options?: { strategicQuestionId?: string }) => void;
}

export function useBiddingChatSession({
  biddingId,
  active,
  clearOnBiddingChange = false,
}: UseBiddingChatSessionParams): BiddingChatSession {
  const dispatch = useDispatch();
  const tab = tabId();

  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [hasNoContent, setHasNoContent] = useState(false);

  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const retriesRef = useRef(0);
  const loadedBiddingRef = useRef(0);

  const { bidding_chat: biddingChat, request } = useSelector<ApplicationState, BiddingChatState>(
    (state) => state.session.bidding_chat,
  );

  const currentChatId = biddingChat.current_chat[tab]?.currentChatId;
  const messages = biddingChat.user_chats[tab]?.user_chat_messages;

  useEffect(() => {
    if (request.fetch_chat[tab]?.loading) setIsLoadingChat(true);
    if (request.fetch_chat[tab]?.loaded || request.fetch_chat[tab]?.error) setIsLoadingChat(false);
    if (request.fetch_chat_messages[tab]?.loading) setIsLoadingChat(true);
    if (request.fetch_chat_messages[tab]?.loaded || request.fetch_chat_messages[tab]?.error) {
      setIsLoadingChat(false);
    }
  }, [request, tab]);

  useEffect(() => {
    if (request.create_chat_message[tab]?.loading) setIsLoadingMessage(true);
    if (request.create_chat_message[tab]?.loaded || request.create_chat_message[tab]?.error) {
      setIsLoadingMessage(false);
    }
  }, [request, tab]);

  useEffect(() => {
    if (biddingId === 0) return;
    if (!active) return;
    if (request.fetch_chat[tab]?.loading) return;

    if (clearOnBiddingChange && loadedBiddingRef.current !== biddingId) {
      if (loadedBiddingRef.current !== 0) dispatch({ type: BiddingChatTypes.CLEAR_CURRENT_CHAT });
      loadedBiddingRef.current = biddingId;
    }

    dispatch(fetchChat(biddingId));
  }, [active, biddingId, dispatch, tab, clearOnBiddingChange]);

  useEffect(() => {
    if (!currentChatId) return;
    setHasNoContent(false);
    dispatch(fetchChatMessages(currentChatId));
  }, [currentChatId, dispatch]);

  useEffect(() => {
    const listEl = messagesRef.current;
    if (!listEl) return;

    listEl.scrollTop = listEl.scrollHeight;
  }, [messages, isLoadingMessage, isLoadingChat, active]);

  useEffect(() => {
    const msgReq = request.fetch_chat_messages?.[tab];
    if (!msgReq) return;

    if (msgReq.loaded || !active) {
      retriesRef.current = 0;
      return;
    }

    if (msgReq.error && retriesRef.current < MAX_FETCH_MESSAGES_RETRIES && currentChatId) {
      retriesRef.current += 1;
      dispatch(fetchChatMessages(currentChatId));
    }
  }, [request, active, tab, currentChatId, dispatch]);

  useEffect(() => {
    if (messages === undefined && Boolean(request.fetch_chat_messages?.[tab]?.loaded)) {
      setHasNoContent(true);
    }
  }, [messages, request.fetch_chat_messages, tab]);

  const sendMessage = (message?: string, options?: { strategicQuestionId?: string }) => {
    const content = (typeof message === 'string' ? message : inputRef.current?.innerText)?.trim();

    if (!content) return;
    if (!currentChatId) return;

    dispatch(
      createChatMessages({
        biddingId,
        chatId: currentChatId,
        content,
        strategicQuestionId: options?.strategicQuestionId,
      }),
    );

    if (inputRef.current) inputRef.current.innerText = '';
  };

  return {
    messages,
    isLoadingChat,
    isLoadingMessage,
    hasNoContent,
    hasFetchMessagesError: Boolean(request.fetch_chat_messages[tab]?.error),
    messagesRef,
    inputRef,
    sendMessage,
  };
}
